import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import classNames from "classnames";
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';

const useStyles = makeStyles(() => ({
  root: {
    flexWrap: "wrap",
    maxWidth: "500px",
  },
  video: {
    width: "100%",
    borderRadius: "10px",
  },
  optionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "calc(50% - 5px)",
    border: "2px solid",
    borderRadius: "10px",
    maxHeight: "300px",
    height: "50vw",
    cursor: "pointer",
    "& svg": {
      fontSize: "100px"
    },
  },
  photoOption: {
    color: "#880e4f",
    borderColor: "#880e4f",
    marginRight: "5px",
    "&:hover": {
      background: "#880e4f",
      color: "#fff"
    }
  },
  videoOption: {
    color: "#b71c1c",
    borderColor: "#b71c1c",
    "&:hover": {
      background: "#b71c1c",
      color: "#fff"
    }
  },
  w100: {
    width: "100%"
  }
  
}));

const CaptureCamera = ({ open, onClose, onSave }) => {
  const classes = useStyles();

  const videoRef = useRef(null);
  const [canUseCamera, setCanUseCamera] = useState(false);
  const streamRef = useRef(null);

  // variaveis para gravar imagem
  const canvasRef = useRef(null);
  const [captureOption, setCaptureOption] = useState(null);

  // variaveis para gravar video
  const [isRecording, setIsRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // === Inicializa e finaliza a câmera ===
  useEffect(() => {
    if (!captureOption) return;

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" } // Prioriza câmera traseira
          },
          audio: captureOption === "video" // ativa áudio apenas quando for vídeo
        });
        streamRef.current = stream;
        setCanUseCamera(true);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (error) {
        toast.error("Erro ao acessar câmera");
        console.error("Camera error:", error);
      }
    };

    initCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [captureOption]);

  const handleClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      if (isRecording) handleStopRecording();
      setCanUseCamera(false);
      setCaptureOption(null);
      setIsRecording(false);
      setVideoUrl(null);

      onClose();
    }
  };

  const handleSave = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const base64 = canvas.toDataURL("image/png");
    onSave(base64);
    handleClose();
  };

  // === Iniciar gravação ===
  const handleStartRecording = () => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: "video/webm;codecs=vp9",
    });

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    };

    recorder.start();
    recorderRef.current = recorder;
    setIsRecording(true);
  };

  // === Parar gravação ===
  const handleStopRecording = () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop();
      setIsRecording(false);

      handleSaveVideo();
    }
  };

  // === Salvar vídeo ===
  const handleSaveVideo = async () => {
    if (!videoUrl) return;

    const response = await fetch(videoUrl);
    const blob = await response.blob();

    // Converte Blob → Base64
    const blobToBase64 = async (blob) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const base64String = await blobToBase64(blob);

    // Envia Base64 para o onSave
    onSave(base64String);

    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{i18n.t("captureCamera.title")}</DialogTitle>

        <DialogContent>
          {
            (captureOption == "photo" || captureOption == "video" ) &&
            <>
              <video ref={videoRef} autoPlay playsInline muted className={classes.video} />
              <canvas ref={canvasRef} style={{ display: "none" }} />
              {
                captureOption == "photo" &&
                <Button
                  onClick={handleSave}
                  color="primary"
                  variant="contained"
                  disabled={!canUseCamera}
                  sx={{ width: "100%" }}
                >
                  {i18n.t("captureCamera.buttons.photo")}
                </Button>
              }
              {
                captureOption == "video" &&
                <>
                  {!isRecording && !videoUrl && (
                    <Button
                      onClick={handleStartRecording}
                      color="primary"
                      variant="contained"
                      disabled={!canUseCamera}
                      className={classes.w100}
                    >
                      {i18n.t("captureCamera.buttons.startRecording")}
                    </Button>
                  )}
                  {isRecording && (
                    <Button 
                      onClick={handleStopRecording} 
                      color="default" 
                      variant="contained"
                      className={classes.w100}
                    >
                      {i18n.t("captureCamera.buttons.stopRecording")}
                    </Button>
                  )}

                  {videoUrl && (
                    <Button 
                      onClick={handleSaveVideo} 
                      color="primary" 
                      variant="contained"
                      className={classes.w100}
                    >
                      {i18n.t("captureCamera.buttons.sendRecording")}
                    </Button>
                  )}
                </>
              }
            </>
          }
          {
            captureOption == null &&
            <>
              <Tooltip
                title={<Typography sx={{ fontSize: "1.5rem" }}>{i18n.t("captureCamera.photo")}</Typography>}
              >
                <Box
                  className={classNames({
                    [classes.optionButton]: true,
                    [classes.photoOption]: true,
                  })}
                  onClick={() => setCaptureOption("photo")}
                >
                  <CameraAltOutlinedIcon />
                </Box>
              </Tooltip>
              <Tooltip
                title={<Typography sx={{ fontSize: "1.5rem" }}>{i18n.t("captureCamera.video")}</Typography>}
              >
                <Box
                  className={classNames({
                    [classes.optionButton]: true,
                    [classes.videoOption]: true,
                  })}
                  onClick={() => setCaptureOption("video")}
                >
                  <VideocamOutlinedIcon />
                </Box>
              </Tooltip>
            </>
          }
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleClose} 
            color="secondary" 
            variant="outlined"
            sx={{ marginRight: "20px" }}
          >
            {i18n.t("buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CaptureCamera;